import React, { useEffect, useState } from 'react';
import Map, { Source, Layer, NavigationControl, Marker, Popup } from 'react-map-gl/mapbox';
import { gpx } from '@tmcw/togeojson';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

const lineStyle = {
  id: 'route-line',
  type: 'line',
  paint: {
    'line-color': '#ff4d4d',
    'line-width': 4,
    'line-opacity': 0.8
  }
};

export default function MapDisplay({ trackUrl, markers = [], zoomOffset = 0 }) {
  const [geoData, setGeoData] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [viewState, setViewState] = useState({
    longitude: -121.7, // Default to a Cascade mountain region roughly
    latitude: 46.8,
    zoom: 11,
    pitch: 60,
    bearing: 0
  });

  useEffect(() => {
    setSelectedMarker(null);
    if (!trackUrl) return;
    
    fetch(trackUrl)
      .then(res => res.text())
      .then(text => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/xml');
        const geojson = gpx(doc);
        setGeoData(geojson);
        
        // Calculate bounding box
        if (geojson.features && geojson.features.length > 0) {
          let minLon = Infinity, maxLon = -Infinity;
          let minLat = Infinity, maxLat = -Infinity;
          
          geojson.features.forEach(f => {
            if (f.geometry && f.geometry.type && f.geometry.type.includes('LineString')) {
              const processCoords = (coords) => {
                if (!coords || coords.length === 0) return;
                if (Array.isArray(coords[0])) {
                  coords.forEach(processCoords); // Recursive for multi-lines
                } else if (coords.length >= 2) {
                  const lon = coords[0];
                  const lat = coords[1];
                  if (lon < minLon) minLon = lon;
                  if (lon > maxLon) maxLon = lon;
                  if (lat < minLat) minLat = lat;
                  if (lat > maxLat) maxLat = lat;
                }
              };
              if (f.geometry && f.geometry.coordinates) {
                processCoords(f.geometry.coordinates);
              }
            }
          });

          if (minLon !== Infinity) {
            const centerLon = (minLon + maxLon) / 2;
            const centerLat = (minLat + maxLat) / 2;
            const maxDiff = Math.max(maxLon - minLon, maxLat - minLat);
            
            let calcZoom = 11.5;
            if (maxDiff > 0.15) calcZoom = 11;      // GP
            else if (maxDiff > 0.06) calcZoom = 11.5; // Cask Lake
            else calcZoom = 12;                     // Mt Hood, Eldorado

            setViewState(prev => ({
              ...prev,
              longitude: centerLon,
              latitude: centerLat,
              zoom: calcZoom + zoomOffset,
              transitionDuration: 2000 // 2 seconds fly animation
            }));
          }
        }
      })
      .catch(err => console.error("Error loading GPX:", err));
  }, [trackUrl]);

  if (!MAPBOX_TOKEN) {
    return (
      <div style={{ padding: '40px 20px', background: 'rgba(17, 17, 17, 0.6)', border: '1px solid #262626', borderRadius: '16px', textAlign: 'center', margin: '20px 0' }}>
        <h3 style={{ marginBottom: '10px' }}>🗺️ 3D Map Component Ready</h3>
        <p style={{ color: '#a3a3a3', marginBottom: '20px' }}>To view the 3D Gaia GPS tracks, you need a Mapbox Access Token.</p>
        <p style={{ fontSize: '0.9rem', color: '#a3a3a3' }}>
          1. Create a free account at <a href="https://mapbox.com" target="_blank" rel="noreferrer" style={{ color: '#00ffcc' }}>mapbox.com</a><br />
          2. Copy your default public token<br />
          3. Create a <code>.env</code> file in the <code>client</code> folder with:<br />
          <code style={{ background: '#000', padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginTop: '10px', color: '#fff' }}>REACT_APP_MAPBOX_TOKEN=pk.eyJ1IjoianJ1Ym93IiwiYSI6ImNtdXBvMmZlNzAyYnkyeW9uODExZDd5ZTgifQ.AwFVt4JD6ks5rqaUvsb_0Q</code>
        </p>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
      <Map
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        mapStyle="mapbox://styles/mapbox/satellite-v9"
        mapboxAccessToken={MAPBOX_TOKEN}
        terrain={{ source: 'mapbox-dem', exaggeration: 1.5 }}
      >
        <Source
          id="mapbox-dem"
          type="raster-dem"
          url="mapbox://mapbox.mapbox-terrain-dem-v1"
          tileSize={512}
          maxzoom={14}
        />
        
        {geoData && (
          <Source id="my-data" type="geojson" data={geoData}>
            <Layer {...lineStyle} />
          </Source>
        )}
        
        {markers.map((marker, index) => (
          <Marker 
            key={index} 
            longitude={marker.longitude} 
            latitude={marker.latitude}
            anchor="bottom"
            onClick={e => {
              e.originalEvent.stopPropagation();
              setSelectedMarker(marker);
            }}
          >
            <div style={{
              cursor: 'pointer',
              background: '#fff',
              border: '2px solid #ff4d4d',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
              overflow: 'hidden'
            }}>
              {marker.image ? (
                <img src={marker.image} alt="marker" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '8px', height: '8px', background: '#ff4d4d', borderRadius: '50%' }} />
              )}
            </div>
          </Marker>
        ))}

        {selectedMarker && (
          <Popup
            longitude={selectedMarker.longitude}
            latitude={selectedMarker.latitude}
            anchor="bottom"
            offset={[0, -30]}
            onClose={() => setSelectedMarker(null)}
            closeOnClick={false}
            maxWidth="300px"
            style={{ overflow: 'hidden' }}
          >
            <div style={{ position: 'relative', width: '250px', display: 'flex' }}>
              {selectedMarker.image && (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <img 
                    src={selectedMarker.image} 
                    alt="popup" 
                    style={{ 
                      width: '100%', 
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                  {selectedMarker.caption && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0, left: 0, right: 0,
                      padding: '40px 16px 16px 16px',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 50%, transparent 100%)',
                      color: '#fff',
                      textAlign: 'left',
                      fontWeight: 500,
                      fontSize: '1rem',
                      letterSpacing: '0.5px'
                    }}>
                      {selectedMarker.caption}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Popup>
        )}

        <NavigationControl visualizePitch={true} />
      </Map>
    </div>
  );
}
